import React, { useState, useEffect } from 'react'
// FaSearch import removed as it's not used
import SearchBar from '../components/SearchBar'
import AssociatedCodes from '../components/AssociatedCodes'
import CodeCard from '../components/CodeCard'
import StepCard from '../components/StepCard'
import BenefitCard from '../components/BenefitCard'
import Faq from '../components/Faq'
import Newsletter from '../components/Newsletter'
import SchemaMarkup from '../components/SchemaMarkup'
import supabase from '../services/supabase'
import '../styles/global.css'

const Home = () => {
  const [popularCodes, setPopularCodes] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [associatedCodes, setAssociatedCodes] = useState([])
  const [sourceType, setSourceType] = useState(null)
  const [sourceCode, setSourceCode] = useState(null)
  const [limit, setLimit] = useState(20)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(true)

  const faqData = [
    {
      question: '¿Qué es el CNAE?',
      answer:
        'La Clasificación Nacional de Actividades Económicas (CNAE) es un sistema de codificación que agrupa las actividades económicas según un criterio de homogeneidad para fines estadísticos.\n\nEstablecido por el Instituto Nacional de Estadística (INE), el CNAE 2025 es la versión actualizada que toda empresa debe utilizar en sus trámites administrativos, declaraciones estadísticas y documentación oficial.',
    },
    {
      question: '¿Qué es el IAE?',
      answer:
        'El Impuesto sobre Actividades Económicas (IAE) es un tributo municipal que grava el ejercicio de actividades empresariales, profesionales o artísticas en territorio español.\n\nCada actividad económica tiene asignado un epígrafe IAE específico que la clasifica fiscalmente según lo establecido por la Agencia Tributaria. Los autónomos y empresas deben seleccionar correctamente sus epígrafes IAE para cumplir con sus obligaciones fiscales.',
    },
    {
      question: '¿Por qué necesito convertir entre códigos IAE y CNAE?',
      answer:
        'Necesitas convertir entre códigos IAE y CNAE para:\n\nCumplir correctamente con tus obligaciones fiscales y administrativas\nCompletar formularios oficiales que requieren ambos códigos\nRealizar trámites ante la Seguridad Social, Hacienda y otros organismos\nAcceder a subvenciones y ayudas donde se requiere la clasificación CNAE\nElaborar informes estadísticos y análisis sectoriales',
    },
    {
      question: '¿Qué diferencia hay entre el IAE y el CNAE?',
      answer:
        'Aunque tanto el IAE como la CNAE sirven para clasificar actividades económicas, la principal diferencia es su aplicación:\n\nEl IAE es un sistema utilizado exclusivamente en España para fines fiscales\nLa CNAE es un sistema europeo utilizado para clasificar actividades en toda la Unión Europea\nEl IAE se enfoca en la recaudación de impuestos\nLa CNAE tiene una aplicación más amplia, que incluye estadísticas y otros fines administrativos',
    },
    {
      question: '¿Cómo sé qué código IAE o CNAE corresponde a mi actividad?',
      answer:
        'Para identificar el código correcto:\n\nUtiliza nuestro conversor introduciendo una descripción de tu actividad\nRevisa las sugerencias automáticas que te ofrece la herramienta\nExamina la descripción detallada de cada código para encontrar el que mejor se ajusta\nVerifica que el código seleccionado incluye todas las actividades que realizas\n\nEn caso de duda, puedes consultar con un asesor fiscal o directamente con la Agencia Tributaria.',
    },
    {
      question: '¿Están actualizados los códigos en el conversor?',
      answer:
        'Sí, nuestra base de datos se actualiza trimestralmente para incluir los últimos cambios en la clasificación CNAE 2025 y los epígrafes IAE vigentes.\n\nNos basamos en las publicaciones oficiales del Instituto Nacional de Estadística (INE) y la Agencia Tributaria para garantizar que trabajas con la información más reciente y exacta.',
    },
    {
      question: '¿Puedo tener más de un código IAE o CNAE?',
      answer:
        'Sí, una empresa o autónomo puede tener varios epígrafes IAE si desarrolla diferentes actividades económicas.\n\nCada actividad debe estar debidamente clasificada con su epígrafe correspondiente. De igual manera, puede tener asociados varios códigos CNAE si opera en distintos sectores económicos.\n\nNuestro conversor te permite identificar todos los códigos necesarios para tu negocio.',
    },
    {
      question:
        '¿Cómo puedo resolver dudas específicas sobre mi clasificación?',
      answer:
        'Si tienes dudas específicas sobre tu clasificación después de usar nuestro conversor:\n\nConsulta la descripción detallada y notas explicativas de cada código\nRevisa las actividades incluidas y excluidas en cada epígrafe\nContacta con un asesor fiscal especializado\nConsulta directamente con la Agencia Tributaria o el Instituto Nacional de Estadística',
    },
    {
      question: '¿Qué ocurre si utilizo un código IAE o CNAE incorrecto?',
      answer:
        'Utilizar un código incorrecto puede tener consecuencias como:\n\nSanciones administrativas por parte de la Agencia Tributaria\nProblemas en la tramitación de licencias y permisos\nDificultades para acceder a subvenciones específicas para tu sector\nComplicaciones en trámites ante la Seguridad Social y otros organismos\nInexactitudes en las estadísticas oficiales relacionadas con tu actividad\n\nPor ello, es fundamental utilizar herramientas fiables como nuestro conversor para identificar correctamente tus códigos.',
    },
    {
      question: '¿Qué es el certificado IAE?',
      answer:
        'El certificado IAE es un documento oficial emitido por la Agencia Tributaria en España que certifica que tu empresa está registrada en el Impuesto sobre Actividades Económicas.\n\nEste certificado es necesario para realizar varios trámites administrativos y fiscales. Obtenerlo es un paso crucial para cumplir con las obligaciones fiscales y demostrar la legalidad de la actividad económica que realizas.',
    },
    {
      question: '¿Cuál es el código CNAE de mi empresa?',
      answer:
        'Para encontrar el código CNAE de tu empresa, puedes usar nuestra herramienta de búsqueda en tiempo real.\n\nSimplemente ingresa el nombre de tu actividad económica o el código IAE conocido, y te proporcionaremos el código CNAE correspondiente. Este código es esencial para asegurarte de que tu empresa esté correctamente registrada y clasificada en el sistema europeo de actividades económicas.',
    },
  ]

  const fetchPopularCodes = async (
    limitParam = limit,
    offsetParam = offset,
    append = false
  ) => {
    try {
      setIsLoading(true)

      // Get most searched codes with pagination
      const { data, error } = await supabase.rpc('get_most_search_codes', {
        limit_param: limitParam,
        offset_param: offsetParam,
      })

      if (error) {
        console.error('Error fetching popular codes:', error)
        throw error
      }

      if (!data || data.length === 0) {
        console.warn('No popular codes found')
        setHasMore(false)
        if (!append) {
          setPopularCodes([])
        }
        return
      }

      // Process the data to format it for the UI
      const formattedData = data.map((item) => {
        return {
          code: item.code,
          search_count: item.search_count,
          title: item.title || 'Sin título disponible',
          type: item.type === 'IAE' ? 'iae' : 'cnae',
          slug: item.slug,
        }
      })

      // If we're appending, add to existing codes, otherwise replace
      if (append) {
        setPopularCodes((prevCodes) => [...prevCodes, ...formattedData])
      } else {
        setPopularCodes(formattedData)
      }

      // Check if we have more data to load
      setHasMore(data.length === limitParam)
    } catch (error) {
      console.error('Error fetching popular codes:', error)
      setError('Error al cargar los códigos populares')
    } finally {
      setIsLoading(false)
    }
  }

  // Load more codes
  const handleLoadMore = () => {
    const newOffset = offset + limit
    setOffset(newOffset)
    fetchPopularCodes(limit, newOffset, true)
  }

  useEffect(() => {
    fetchPopularCodes()
  }, [])

  // Handle conversion callback from SearchBar
  function handleConversion(associatedCodesData, type, code) {
    setAssociatedCodes(associatedCodesData)
    setSourceType(type)
    setSourceCode(code)

    // Scroll to the top to show the associated codes
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <div className='container mx-auto px-4'>
      {/* Header Section */}
      <header className='py-8 md:py-16 text-center'>
        <h1 className='text-2xl md:text-4xl font-bold mb-4 text-text-primary'>
          Conversor de Códigos IAE y CNAE para Autónomos y Empresas
        </h1>
        <p className='text-text-secondary text-lg mb-8 max-w-2xl mx-auto'>
          Herramienta para convertir epígrafes IAE a códigos CNAE 2025 y
          viceversa. Encuentra la equivalencia entre códigos de actividades
          económicas actualizados.
        </p>

        {/* Search Bar */}
        <div className='max-w-xl mx-auto mb-6'>
          <SearchBar onConversion={handleConversion} />
        </div>

        {/* Sponsor Banner */}
        <div className='max-w-xl mx-auto mb-8'>
          <a
            href='/patrocinadores/'
            className='block bg-surface border border-primary rounded-md p-4 text-center hover:border-secondary transition-all'
            style={{ backgroundColor: 'rgba(76, 175, 80, 0.1)' }}
          >
            <div className='flex items-center justify-center gap-4 mb-1'>
              <h3 className='text-xl font-bold text-primary'>
                PON TU MARCA AQUÍ
              </h3>
              <svg
                width='60'
                height='28'
                viewBox='0 0 60 28'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M1.5 3C14.1699 2.61606 31.7609 -2.78055 41.7778 7.75C45.9047 12.0886 47.5 14.1815 47.5 20'
                  stroke='white'
                  strokeWidth='1.3'
                  strokeLinecap='round'
                />
                <path
                  d='M36 17.5C37.2616 18.9018 38.2923 20.0443 39.75 21.2778C41.876 23.0767 42.4326 25.4127 45.4444 26.4167C49.8497 27.8851 56.2455 20.9305 59 18.5'
                  stroke='white'
                  strokeWidth='1.3'
                  strokeLinecap='round'
                />
              </svg>
            </div>
            <p className='text-sm text-text-secondary'>
              Llega a cientos de profesionales y empresas cada día
            </p>
          </a>
        </div>

        {/* Associated Codes */}
        <div className='max-w-3xl mx-auto'>
          {associatedCodes.length > 0 && (
            <AssociatedCodes
              associatedCodes={associatedCodes}
              sourceType={sourceType}
              sourceCode={sourceCode}
            />
          )}
        </div>
      </header>

      {/* Always show these sections regardless of associatedCodes */}
      <>
        {/* Popular Codes Section */}
        <section className='py-8 md:py-12'>
          <h2 className='text-xl md:text-3xl font-bold mb-6 text-text-primary text-center'>
            Códigos CNAE y Epígrafes IAE Más Populares
          </h2>

          {/* ItemList Schema for popular codes */}
          {popularCodes.length > 0 && (
            <SchemaMarkup
              type='ItemList'
              data={{
                name: 'Códigos CNAE y Epígrafes IAE Más Populares',
                description:
                  'Lista de los códigos CNAE y epígrafes IAE más buscados en España',
                items: popularCodes.map((code) => ({
                  title: `${code.code} - ${code.title}`,
                  code: code.code,
                  url: `https://conversoriaecnae.es/${
                    code.type === 'iae'
                      ? 'iae'
                      : code.type === 'cnae'
                        ? 'cnae-2025'
                        : 'cnae-2009'
                  }/${code.code.replace('.', '')}/${code.slug}`,
                })),
              }}
            />
          )}

          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
            {isLoading && popularCodes.length === 0 ? (
              <div className='col-span-full flex justify-center'>
                <div className='w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin'></div>
              </div>
            ) : error ? (
              <div className='col-span-full text-center text-red-500'>
                {error}
              </div>
            ) : (
              popularCodes.map((code, index) => (
                <CodeCard
                  key={index}
                  code={code.code}
                  title={code.title}
                  type={code.type}
                  slug={code.slug}
                  searchCount={code.search_count}
                />
              ))
            )}
          </div>

          {/* Load More Button */}
          {hasMore && popularCodes.length > 0 && (
            <div className='flex justify-center mt-8'>
              <button
                onClick={handleLoadMore}
                className='px-6 py-2 bg-surface text-white rounded-md hover:bg-primary transition-colors duration-300 flex items-center'
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className='mr-2'>Cargando...</span>
                    <div className='w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin'></div>
                  </>
                ) : (
                  'Cargar Más Códigos'
                )}
              </button>
            </div>
          )}
        </section>

        {/* How It Works Section */}
        <section className='py-8 md:py-16 text-center'>
          <h2 className='text-xl md:text-3xl font-bold mb-4 text-text-primary'>
            ¿Cómo Funciona el Conversor IAE CNAE?
          </h2>
          <p className='text-text-secondary text-base font-medium mb-20 max-w-2xl mx-auto'>
            Transforma instantáneamente tus epígrafes IAE a códigos CNAE 2025 y
            viceversa con nuestro conversor especializado. Nuestra herramienta
            te ofrece la equivalencia exacta entre códigos fiscales en segundos.
            Descubre paso a paso cómo utilizar nuestro conversor online, ¡sin
            complicaciones!
          </p>

          {/* HowTo Schema */}
          <SchemaMarkup
            type='HowTo'
            data={{
              name: '¿Cómo Funciona el Conversor IAE CNAE?',
              description:
                'Transforma instantáneamente tus epígrafes IAE a códigos CNAE 2025 y viceversa con nuestro conversor especializado.',
              steps: [
                {
                  title: 'Realiza tu búsqueda de códigos',
                  description:
                    'Comienza escribiendo el código o epígrafe IAE o CNAE en nuestro buscador inteligente. La herramienta mostrará sugerencias automáticas mientras escribes, facilitándote encontrar rápidamente el epígrafe o código exacto que necesitas para tu actividad económica.',
                },
                {
                  title: 'Selecciona el código o epígrafe correcto',
                  description:
                    'Elige el epígrafe fiscal que corresponde a tu actividad empresarial entre las opciones sugeridas. Accederás inmediatamente a todos los detalles relacionados con el código IAE o epígrafe CNAE seleccionado, incluyendo su clasificación oficial.',
                },
                {
                  title: 'Convierte y consulta los detalles completos',
                  description:
                    'Haz clic en "Convertir" para visualizar la equivalencia exacta entre el epígrafe IAE y código CNAE correspondiente. Obtén acceso a la descripción completa, categorías, nivel, códigos asociados y toda la información relevante para cada código, facilitando tus trámites administrativos y fiscales.',
                },
              ],
            }}
          />

          <div className='grid grid-cols-1 md:grid-cols-3 gap-8 mb-12'>
            <StepCard
              number='1'
              title='Realiza tu búsqueda de códigos'
              description='Comienza escribiendo el código o epígrafe IAE o CNAE en nuestro buscador inteligente. La herramienta mostrará sugerencias automáticas mientras escribes, facilitándote encontrar rápidamente el epígrafe o código exacto que necesitas para tu actividad económica.'
            />
            <StepCard
              number='2'
              title='Selecciona el código o epígrafe correcto'
              description='Elige el epígrafe fiscal que corresponde a tu actividad empresarial entre las opciones sugeridas. Accederás inmediatamente a todos los detalles relacionados con el código IAE o epígrafe CNAE seleccionado, incluyendo su clasificación oficial.'
            />
            <StepCard
              number='3'
              title='Convierte y consulta los detalles completos'
              description='Haz clic en "Convertir" para visualizar la equivalencia exacta entre el epígrafe IAE y código CNAE correspondiente. Obtén acceso a la descripción completa, categorías, nivel, códigos asociados y toda la información relevante para cada código, facilitando tus trámites administrativos y fiscales.'
            />
          </div>

          <div className='max-w-3xl mx-auto'>
            <img
              src='/resources/images/conversoriaecnae.gif'
              alt='Cómo funciona el conversor IAE CNAE GIF'
              className='w-full rounded-lg shadow-lg'
            />
          </div>
        </section>

        {/* Benefits Section */}
        <section className='py-8 md:py-16'>
          <h2 className='text-xl md:text-3xl font-bold mb-4 text-text-primary text-center'>
            Beneficios de Usar Nuestro Conversor IAE CNAE
          </h2>
          <p className='text-text-secondary font-medium mb-12 max-w-2xl mx-auto text-center'>
            Accede a la base de datos más completa y actualizada de epígrafes
            IAE y códigos CNAE de España. Convierte de forma instantánea,
            precisa y con toda la información oficial relevante para tu
            actividad económica.
          </p>

          <div className='grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto'>
            <BenefitCard
              icon='/resources/icons/database.png'
              title='Base de datos más completa y extensa de España'
              description='Accede a 1010 códigos CNAE 2025 y 1419 epígrafes IAE oficiales de forma rápida y directa. Nuestra herramienta garantiza que encuentres siempre la equivalencia exacta entre códigos fiscales para tu actividad empresarial.'
            />
            <BenefitCard
              icon='/resources/icons/glass.png'
              title='Precisión garantizada en cada conversión'
              description='Todos los datos y las relaciones entre epígrafes IAE y códigos CNAE han sido verificados y revisados manualmente por expertos fiscales, proporcionándote resultados 100% exactos y fiables para tus trámites administrativos.'
            />
            <BenefitCard
              icon='/resources/icons/setting.png'
              title='Soporte y actualización continua de epígrafes fiscales'
              description='Nuestra base de datos se actualiza cada 3 meses con las últimas modificaciones legislativas, asegurando que siempre estés trabajando con la información fiscal más reciente, actualizada y precisa para tu negocio.'
            />
            <BenefitCard
              icon='/resources/icons/time.png'
              title='Ahorro de tiempo y facilidad en la búsqueda de códigos'
              description='Localiza los epígrafes IAE y códigos CNAE en segundos, sin perder tiempo consultando listados oficiales. Nuestro conversor es intuitivo, rápido y no requiere conocimientos técnicos previos para obtener la equivalencia exacta.'
            />
          </div>
        </section>

        {/* Newsletter Section */}
        <Newsletter />

        {/* FAQ Section */}
        <Faq
          title='Preguntas Frecuentes sobre el Conversor IAE CNAE (FAQ)'
          description='Resolvemos tus dudas sobre los epígrafes IAE, códigos CNAE y cómo utilizar nuestro conversor especializado. Toda la información proporcionada está basada en fuentes oficiales y actualizada para 2025.'
          faqData={faqData}
        />

        {/* FAQPage Schema */}
        <SchemaMarkup
          type='FAQPage'
          data={{
            questions: faqData.map((faq) => ({
              question: faq.question,
              answer:
                typeof faq.answer === 'string'
                  ? faq.answer.replace(/\n\n/g, ' ').replace(/\n/g, ' ')
                  : `${faq.answer.intro} ${
                      faq.answer.bullets ? faq.answer.bullets.join(' ') : ''
                    }`,
            })),
          }}
        />
      </>
    </div>
  )
}

export default Home
